import {isFeatureEnabled} from '@github-ui/feature-flags'
import {updateUrl} from '@github-ui/history'
import {createContext, use, useEffect, useMemo, useReducer, useRef, type ReactNode, type RefObject} from 'react'

import {measureIsWideViewport, DIFF_COMPARISON_VIEWER_CONTAINER_ID} from '../helpers/measure-viewport'

export const DockedPanel = {
  AgentSessions: 'agentSessions',
  Comments: 'comments',
  Alerts: 'alerts',
  Info: 'info',
  Overview: 'overview',
  MergeStatus: 'mergeStatus',
} as const

export type DockedPanel = (typeof DockedPanel)[keyof typeof DockedPanel]
export type NullableDockedPanel = DockedPanel | null

export interface CommentsPanelMetadata {
  selectedAuthor?: string
  hideResolved?: boolean
}

// Union of all panel metadata types — extend as more panels need metadata
export type PanelMetadata = CommentsPanelMetadata
export type NullablePanelMetadata = PanelMetadata | null

export interface PanelOpenOptions {
  returnFocusTo?: HTMLElement | null
  metadata?: PanelMetadata
}

interface DockedPanelState {
  activePanel: NullableDockedPanel
  isCompact: boolean
  renderActive: boolean
  metadata: NullablePanelMetadata
}

type DockedPanelAction =
  | {type: 'OPEN_PANEL'; panel: DockedPanel; isCompact: boolean; metadata: NullablePanelMetadata}
  | {type: 'CLOSE_PANEL'}
  | {type: 'HIDE_FOR_RESIZE'}
  | {type: 'SHOW_ON_RESIZE'}
  | {type: 'TOGGLE_PANEL'; panel: DockedPanel; isCompact: boolean; metadata: NullablePanelMetadata}

const initialState: DockedPanelState = {
  activePanel: null,
  isCompact: false,
  renderActive: false,
  metadata: null,
}

function dockedPanelReducer(state: DockedPanelState, action: DockedPanelAction): DockedPanelState {
  switch (action.type) {
    case 'OPEN_PANEL':
      return {activePanel: action.panel, isCompact: action.isCompact, renderActive: true, metadata: action.metadata}
    case 'CLOSE_PANEL':
      return initialState
    case 'HIDE_FOR_RESIZE':
      return {...state, renderActive: false}
    case 'SHOW_ON_RESIZE':
      return {...state, renderActive: true}
    case 'TOGGLE_PANEL':
      if (state.activePanel === action.panel && state.renderActive) {
        return initialState
      }
      return {activePanel: action.panel, isCompact: action.isCompact, renderActive: true, metadata: action.metadata}
  }
}

interface DockedPanelActions {
  openPanel: (panel: DockedPanel, options?: PanelOpenOptions) => void
  closePanel: () => void
  togglePanel: (panel: DockedPanel, options?: PanelOpenOptions) => void
}

export interface DockedPanelFocusRefs {
  /** Ref to the close button inside the Panel component */
  closeButtonRef: RefObject<HTMLButtonElement | null>
  /** Ref to the element that should receive focus when the panel closes */
  returnFocusRef: RefObject<HTMLElement | null>
}

// Split contexts to prevent unnecessary re-renders:
// - Components that only need to trigger actions won't re-render when panel state changes
// - Components that need to read state will subscribe to state context
export const DockedPanelStateContext = createContext<DockedPanelState>(initialState)

const defaultActions: DockedPanelActions = {
  openPanel: () => undefined,
  closePanel: () => undefined,
  togglePanel: () => undefined,
}

export const DockedPanelDispatchContext = createContext<DockedPanelActions>(defaultActions)

const defaultFocusRefs: DockedPanelFocusRefs = {
  closeButtonRef: {current: null},
  returnFocusRef: {current: null},
}

export const DockedPanelFocusContext = createContext<DockedPanelFocusRefs>(defaultFocusRefs)

const DEFAULT_ENABLED_PANELS: DockedPanel[] = []

export const DockedPanelEnabledContext = createContext<DockedPanel[]>(DEFAULT_ENABLED_PANELS)

interface DockedPanelProviderProps {
  children: ReactNode
  isOnSupportedRoute?: boolean
  enabledPanels?: DockedPanel[]
}

const PANEL_PARAM = 'panel'
const VALID_PANELS: ReadonlySet<string> = new Set<string>(Object.values(DockedPanel))

function parsePanelParam(value: string | null): DockedPanel | null {
  return value !== null && VALID_PANELS.has(value) ? (value as DockedPanel) : null
}

export function DockedPanelProvider({
  children,
  isOnSupportedRoute = false,
  enabledPanels = DEFAULT_ENABLED_PANELS,
}: DockedPanelProviderProps) {
  const isPanelUrlParamEnabled = isFeatureEnabled('prs_url_search_param_panels')

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  const [state, dispatch] = useReducer(dockedPanelReducer, initialState)

  // Restore panel from URL param after mount, when DOM elements (portal
  // targets, measurement containers) are available.  Running in the reducer
  // initializer caused SSR crashes (bare `document` access) and also ran
  // before the portal target was in the DOM, falling through to Dialog mode.
  const hasRestoredRef = useRef(false)
  useEffect(() => {
    if (hasRestoredRef.current || !isPanelUrlParamEnabled) return
    hasRestoredRef.current = true
    const urlParams = new URLSearchParams(window.location.search)
    const panel = parsePanelParam(urlParams.get(PANEL_PARAM))
    if (panel) {
      const isWide = measureIsWideViewport()
      dispatch({type: 'OPEN_PANEL', panel, isCompact: !isWide, metadata: null})
    }
  }, [isPanelUrlParamEnabled])

  // Mirror state in refs so memoized action callbacks can read values synchronously
  const stateRef = useRef(state)

  // Keep stateRef in sync with reducer state after each update.
  // Using useEffect (not render-time assignment) to satisfy React Compiler rules.
  useEffect(() => {
    stateRef.current = state
  }, [state])

  // Sync panel state → URL when URL param syncing is enabled.
  // Uses @github-ui/history's updateUrl (replaceState) to update the URL
  // without triggering React Router's navigation pipeline, soft-nav progress
  // bars, or unnecessary re-renders.
  useEffect(() => {
    if (!isPanelUrlParamEnabled) return
    const urlParams = new URLSearchParams(window.location.search)
    const currentUrlPanel = parsePanelParam(urlParams.get(PANEL_PARAM))
    if (state.activePanel === currentUrlPanel) return

    if (state.activePanel) {
      urlParams.set(PANEL_PARAM, state.activePanel)
    } else {
      urlParams.delete(PANEL_PARAM)
    }
    const search = urlParams.toString()
    const newUrl = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`
    updateUrl(newUrl)
  }, [state.activePanel, isPanelUrlParamEnabled])

  // Memoize actions object to keep dispatch context value stable
  // This ensures components using only useDockedPanelActions() won't re-render on state changes
  const actions = useMemo<DockedPanelActions>(
    () => ({
      openPanel: (panel: DockedPanel, options?: PanelOpenOptions) => {
        if (options?.returnFocusTo) returnFocusRef.current = options.returnFocusTo
        const isWide = measureIsWideViewport()
        dispatch({type: 'OPEN_PANEL', panel, isCompact: !isWide, metadata: options?.metadata ?? null})
        // Only focuses the close button if the panel will be visible (wide viewport).
        // On compact viewports the panel shows as a dialog which manages its own focus.
        if (isWide) {
          requestAnimationFrame(() => closeButtonRef.current?.focus())
        }
      },
      closePanel: () => {
        dispatch({type: 'CLOSE_PANEL'})
      },
      togglePanel: (panel: DockedPanel, options?: PanelOpenOptions) => {
        if (options?.returnFocusTo) returnFocusRef.current = options.returnFocusTo
        const isWide = measureIsWideViewport()
        dispatch({type: 'TOGGLE_PANEL', panel, isCompact: !isWide, metadata: options?.metadata ?? null})
        // Focus the close button when opening/switching (not closing) in wide mode
        if (stateRef.current.activePanel !== panel && isWide) {
          requestAnimationFrame(() => closeButtonRef.current?.focus())
        }
      },
    }),
    [returnFocusRef, closeButtonRef],
  )

  // Auto-close the panel when the container shrinks below the breakpoint while in docked (non-compact) mode.
  // A panel opened in compact (dialog) mode is unaffected.
  // Uses ResizeObserver on the container to detect when the side panel changes available width.
  // Falls back to observing documentElement for actual viewport resizes.
  useEffect(() => {
    function handleResize() {
      // Short circuit to avoid measuring viewport and dispatching if activePanel is not defined
      if (!stateRef.current.activePanel) return

      const isWide = measureIsWideViewport()

      if (!stateRef.current.isCompact && !isWide) {
        dispatch({type: 'HIDE_FOR_RESIZE'})
      }

      if (stateRef.current.renderActive === false && isWide) {
        dispatch({type: 'SHOW_ON_RESIZE'})
      }
    }

    const container = document.getElementById(DIFF_COMPARISON_VIEWER_CONTAINER_ID)
    let observer: ResizeObserver | null = null
    if (container) {
      observer = new ResizeObserver(handleResize)
      observer.observe(container)
    }

    return () => {
      observer?.disconnect()
    }
  }, [])

  // Stable ref object - refs don't change identity so this is safe to memoize once
  const focusRefs = useMemo<DockedPanelFocusRefs>(() => ({closeButtonRef, returnFocusRef}), [])

  // Only expose the panel state when on a supported route (files changed).
  // On other routes the panel is effectively closed, hiding the sidebar.
  const effectiveState = isOnSupportedRoute ? state : initialState

  return (
    <DockedPanelEnabledContext value={enabledPanels}>
      <DockedPanelStateContext value={effectiveState}>
        <DockedPanelDispatchContext value={actions}>
          <DockedPanelFocusContext value={focusRefs}>{children}</DockedPanelFocusContext>
        </DockedPanelDispatchContext>
      </DockedPanelStateContext>
    </DockedPanelEnabledContext>
  )
}

/** Returns memoized actions to control the docked panel - stable reference, won't cause re-renders */
export function useDockedPanelActions(): DockedPanelActions {
  return use(DockedPanelDispatchContext)
}

/** Returns whether any docked panel is currently open on a supported route (files changed) */
export function useIsDockedPanelOpen(dockedPanelType?: DockedPanel | undefined): boolean {
  const context = use(DockedPanelStateContext)
  if (dockedPanelType) {
    return context.activePanel === dockedPanelType && context.renderActive
  }

  return context.activePanel !== null && context.renderActive
}

export function useActiveDockedPanel(): NullableDockedPanel {
  return use(DockedPanelStateContext).activePanel
}

/** Returns shared focus refs for managing focus between panel openers and the close button */
export function useDockedPanelFocusRefs(): DockedPanelFocusRefs {
  return use(DockedPanelFocusContext)
}

/** Returns whether the panel is in compact mode (container < 1012px) and showing as a dialog overlay */
export function useIsDockedPanelCompact(): boolean {
  return use(DockedPanelStateContext).isCompact
}

export function useEnabledDockedPanels(): DockedPanel[] {
  return use(DockedPanelEnabledContext)
}

/** Returns the metadata passed to the most recent openPanel/togglePanel call, or null */
export function useDockedPanelMetadata(): NullablePanelMetadata {
  return use(DockedPanelStateContext).metadata
}
